// THE COMMAND DECK's geometry (#6778, prototype `:3842-3970`): where every column, lane
// and pill sits, as pure functions of the canvas width and the model. The painter
// (`fleet-live-deck-paint.ts`) draws what these place; neither reads the DOM.
//
// THE NUMBERS ARE THE PROTOTYPE'S: a 50 px header band; three intake lanes, then the
// "MACHINES" label, then one lane per shown machine (at most 16); a lane of 26 px past 12
// machines, 28 px in a compact canvas (under 700 px) and 32 px otherwise; a 128 px label
// column (92 compact) and a 104 px right gutter (44 compact). A column under 76 px takes the
// short stage label and one under 44 px only its number; a pill in a column under 74 px
// reads its number alone. Items sit at their stage's column start: the wire has a stage but
// no fraction of it, so nothing simulates progress inside one (brief.md item 47).

import { FLEET_LIVE_STAGES } from '@developerz/domain';
import { ellipsize } from '@developerz/kit';
import { t } from '../i18n';
import { type LiveItem, STAGE_LABEL, STAGE_SHORT } from './fleet-live';
import type { Paint2D } from './fleet-live-canvas';

export const DECK_TOP = 50;
export const INTAKE_LANES = 3;
export const COMPACT_BELOW = 700;
/** A pill's height and corner radius. */
export const PILL_H = 20;
export const PILL_R = 10;
/** The gap kept between two pills in one lane. */
export const PILL_GAP = 6;

export type DeckGeo = {
  readonly w: number;
  /** The drawing's height (the canvas is 8 px taller). */
  readonly h: number;
  readonly lane: number;
  readonly left: number;
  readonly x0: number;
  readonly x1: number;
  /** One stage column's width. */
  readonly cw: number;
  readonly compact: boolean;
};

export function deckGeometry(width: number, machines: number): DeckGeo {
  const compact = width < COMPACT_BELOW;
  const lane = machines > 12 ? 26 : compact ? 28 : 32;
  const left = compact ? 92 : 128;
  const x0 = left + 6;
  const x1 = width - (compact ? 44 : 104);
  return {
    w: width,
    h: DECK_TOP + (INTAKE_LANES + machines) * lane + 24,
    lane,
    left,
    x0,
    x1,
    cw: Math.max(0, (x1 - x0) / FLEET_LIVE_STAGES.length),
    compact,
  };
}

/** The centre line of lane `row` (intake lanes first; machine lanes sit 14 px lower). */
export function laneY(g: DeckGeo, row: number): number {
  return DECK_TOP + row * g.lane + g.lane / 2 + (row >= INTAKE_LANES ? 14 : 0);
}

/**
 * A stage column's label lines, upper-cased after `t()`: the full name, the short one under
 * 76 px, none under 44 px (the number alone), and a label wider than its column wrapped to
 * two lines at the middle word.
 */
export function stageLabelLines(
  g: DeckGeo,
  stage: number,
  measure: (s: string) => number,
): string[] {
  if (g.cw < 44) return [];
  const name = FLEET_LIVE_STAGES[stage];
  if (name === undefined) return [];
  const label = (g.cw < 76 ? STAGE_SHORT[name]() : STAGE_LABEL[name]()).toUpperCase();
  if (measure(label) <= g.cw - 12) return [label];
  const words = label.split(' ');
  if (words.length < 2) return [label];
  const half = Math.ceil(words.length / 2);
  return [words.slice(0, half).join(' '), words.slice(half).join(' ')];
}

/** One pill, placed: its left edge is `x - 10`, its dot sits at `x`. */
export type PlacedPill = {
  readonly item: LiveItem;
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
};

/** Where an item's dot belongs before lane packing. */
export function pillTarget(g: DeckGeo, item: LiveItem): { x: number; y: number } {
  const x = g.x0 + item.stage * g.cw + 14;
  const row = item.machine >= 0 ? INTAKE_LANES + item.machine : item.intake;
  return { x, y: laneY(g, row) };
}

/** A pill's width past its text: 10 px before the dot, the dot, and the text's padding. */
export const PILL_CHROME = 30;
/** A "+N" pill's width past its text. */
export const OVERFLOW_CHROME = 20;
/** The space kept between a pill and the canvas's right edge. */
export const EDGE_PAD = 4;

/**
 * A lane's "+N" pill: the items its lane could not fit (#6778, review 4404758128). `x` is
 * its LEFT edge, `y` the lane's centre line. Its tooltip and its button list `hidden`.
 */
export type OverflowPill = {
  /** Stable per lane: the lane's centre line, rounded. */
  readonly key: string;
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly hidden: readonly LiveItem[];
};

/** What the deck draws: the pills that fit, and one "+N" per crowded lane. */
export type DeckLayout = {
  readonly pills: readonly PlacedPill[];
  readonly overflow: readonly OverflowPill[];
};

/** Every lane's horizontal box: from the first stage column to the canvas's right edge. */
export function laneBounds(g: DeckGeo): { readonly left: number; readonly right: number } {
  return { left: g.x0, right: g.w - EDGE_PAD };
}

/** `text`, cut with an ellipsis until it measures at most `room` (never under 2 characters). */
export function fitText(text: string, room: number, measure: (s: string) => number): string {
  if (measure(text) <= room) return text;
  for (let n = text.length - 1; n > 2; n -= 1) {
    const cutText = ellipsize(text, { max: n });
    if (measure(cutText) <= room) return cutText;
  }
  return ellipsize(text, { max: 2 });
}

type Draft = { item: LiveItem; text: string; x: number; y: number; w: number; readonly tx: number };

/** Pack a lane right to left from each pill's target; answers how many fit at or past `floor`. */
function pack(lane: readonly Draft[], floor: number): number {
  let edge = Number.POSITIVE_INFINITY;
  let fit = 0;
  for (const p of lane) {
    p.x = p.tx;
    if (p.x - 10 + p.w + PILL_GAP > edge) p.x = edge - p.w - PILL_GAP + 10;
    if (p.x - 10 < floor) return fit;
    edge = p.x - 10;
    fit += 1;
  }
  return fit;
}

/**
 * Every item placed (prototype `:3925`): pills sharing a lane pushed left so they never
 * overlap, the rightmost keeping its place and each next one ending `PILL_GAP` before it.
 * The prototype packs with no floor; here a lane is a BOX (`laneBounds`): a title is cut to
 * fit before the right edge, and when the lane cannot hold every pill it shows the ones that
 * fit and ends in one "+N" pill at its left edge holding the rest, so nothing is drawn off
 * the canvas and nothing is dropped (brief.md item 66).
 */
export function layoutLanes(
  g: DeckGeo,
  items: readonly LiveItem[],
  measure: (s: string) => number,
): DeckLayout {
  const tiny = g.cw < 74;
  const { left, right } = laneBounds(g);
  const drafts = items.map((item): Draft => {
    const at = pillTarget(g, item);
    const text = fitText(tiny ? item.tag : item.label, right - (at.x - 10) - PILL_CHROME, measure);
    return { item, text, x: at.x, y: at.y, w: measure(text) + PILL_CHROME, tx: at.x };
  });
  const lanes = new Map<number, Draft[]>();
  for (const d of drafts) {
    const key = Math.round(d.y);
    const lane = lanes.get(key) ?? [];
    lane.push(d);
    lanes.set(key, lane);
  }
  const hidden = new Set<Draft>();
  const overflow: OverflowPill[] = [];
  for (const [key, lane] of lanes) {
    lane.sort((a, b) => b.tx - a.tx || a.item.id.localeCompare(b.item.id));
    if (pack(lane, left) === lane.length) continue;
    // Reserve the "+N" at the lane's left edge (sized for the widest count it can show),
    // then keep the pills that still fit to its right.
    const reserve = measure(overflowText(lane.length)) + OVERFLOW_CHROME;
    const fit = pack(lane, left + reserve + PILL_GAP);
    const rest = lane.slice(fit);
    for (const d of rest) hidden.add(d);
    const text = overflowText(rest.length);
    const y = rest[0]?.y ?? laneY(g, 0);
    overflow.push({
      key: `lane:${key}`,
      text,
      x: left,
      y,
      w: measure(text) + OVERFLOW_CHROME,
      hidden: rest.map((d) => d.item),
    });
  }
  const pills = drafts
    .filter((d) => !hidden.has(d))
    .map(({ item, text, x, y, w }) => ({ item, text, x, y, w }));
  return { pills, overflow };
}

function overflowText(n: number): string {
  return t('fleet.live.overflow_pill', { count: n });
}

/** The pills that fit (the "+N" pills are `layoutLanes`'s `overflow`). */
export function placePills(
  g: DeckGeo,
  items: readonly LiveItem[],
  measure: (s: string) => number,
): PlacedPill[] {
  return [...layoutLanes(g, items, measure).pills];
}

/** The "+N" pill under a point. */
export function overflowAt(
  overflow: readonly OverflowPill[],
  x: number,
  y: number,
): OverflowPill | null {
  for (const o of overflow) {
    const top = o.y - PILL_H / 2;
    if (x >= o.x && x <= o.x + o.w && y >= top && y <= top + PILL_H) return o;
  }
  return null;
}

/** The pill under a point, topmost first (the tooltip's hit test). */
export function pillAt(pills: readonly PlacedPill[], x: number, y: number): PlacedPill | null {
  for (let i = pills.length - 1; i >= 0; i -= 1) {
    const p = pills[i];
    if (p === undefined) continue;
    const left = p.x - 10;
    const top = p.y - PILL_H / 2;
    if (x >= left && x <= left + p.w && y >= top && y <= top + PILL_H) return p;
  }
  return null;
}

/** A measurer for a context set to the pills' font. */
export function measurer(ctx: Paint2D, font: string): (s: string) => number {
  return (s) => {
    ctx.font = font;
    return ctx.measureText(s).width;
  };
}
